import {
  AUTOMACAO_MAX_BOTOES,
  type AutomationTrigger,
  type BotaoDoFluxo,
  type ConfigGatilho,
  type ConfigMensagem,
  capacidadesDoCanal,
  GATILHOS_DE_EVENTO,
  GATILHOS_DE_MENSAGEM,
} from '@empire/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useRef, useState } from 'react'
import { BotaoAcao } from '../../components/BotaoAcao'
import { ContatosDaAutomacao } from '../../components/fluxo/ContatosDaAutomacao'
import { InserirVariavel } from '../../components/InserirVariavel'
import { Modal } from '../../components/Modal'
import { t } from '../../lib/i18n'
import { useWorkspaceRole } from '../../lib/role'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/automacoes/')({
  component: AutomacoesPage,
  /**
   * `?simples=<id>` abre o formulário simples de um fluxo existente.
   *
   * Vive na URL e não em estado local porque quem chega aqui vem do botão
   * "modo simples" lá do canvas — e voltar pelo botão do navegador precisa
   * desfazer isso, não deixar o formulário aberto por cima da lista.
   */
  validateSearch: (busca: Record<string, unknown>) => ({
    simples: typeof busca.simples === 'string' ? busca.simples : undefined,
  }),
})

/** Em português; traduzidos na hora de desenhar a grade de expediente. */
const DIAS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']

/**
 * Título e ajuda de cada gatilho, em português — `t()` entra na hora de
 * desenhar. Traduzidos aqui, seriam resolvidos na importação do arquivo.
 */
const GATILHOS: Record<AutomationTrigger, { titulo: string; ajuda: string }> = {
  first_inbound: {
    titulo: 'Primeira mensagem',
    ajuda: 'Dispara quando alguém escreve pela primeira vez nesta conversa.',
  },
  start_command: {
    titulo: 'Entrou pelo link (/start)',
    ajuda: 'Dispara para quem chega por um link de campanha, antes de escrever qualquer coisa.',
  },
  palavra_chave: {
    titulo: 'Palavra-chave',
    ajuda: 'Dispara quando a mensagem contém uma das palavras. Ignora acento e maiúscula.',
  },
  fora_do_horario: {
    titulo: 'Fora do expediente',
    ajuda: 'Dispara fora do horário de atendimento do inbox.',
  },
  contato_criado: {
    titulo: 'Contato criado',
    ajuda:
      'Dispara quando a pessoa entra no CRM — inclusive por importação. Diferente de “primeira mensagem”: aquele é por conversa, este é por pessoa, uma vez na vida.',
  },
  tag_aplicada: {
    titulo: 'Tag adicionada',
    ajuda: 'Dispara quando a tag é adicionada ao contato, por alguém da equipe ou por outro fluxo.',
  },
  tag_removida: {
    titulo: 'Tag removida',
    ajuda: 'Dispara quando a tag sai do contato.',
  },
  campo_alterado: {
    titulo: 'Campo alterado',
    ajuda: 'Dispara quando um campo do perfil muda de valor.',
  },
  etapa_alterada: {
    titulo: 'Etapa alterada',
    ajuda: 'Dispara quando a conversa muda de etapa no pipeline.',
  },
  responsavel_alterado: {
    titulo: 'Responsável alterado',
    ajuda: 'Dispara quando a conversa troca de dono, ou fica sem.',
  },
  link_clicado: {
    titulo: 'Clicou no link',
    ajuda: 'Dispara quando o contato abre um link rastreado de botão.',
  },
  // Fora dos grupos do editor simples de propósito: pede etapa e tempo, que só
  // o construtor de fluxo tem onde escolher. Fica aqui para a lista saber o nome.
  tempo_na_etapa: {
    titulo: 'Ficou um tempo na etapa',
    ajuda: 'Dispara quando a conversa completa o tempo escolhido parada na mesma etapa.',
  },
}

/** Título do gatilho, traduzido na hora de desenhar. */
function tituloDoGatilho(chave: AutomationTrigger): string {
  const entrada = GATILHOS[chave]
  return entrada ? t(entrada.titulo) : chave
}

/** Ajuda do gatilho, idem. */
function ajudaDoGatilho(chave: AutomationTrigger): string | undefined {
  const entrada = GATILHOS[chave]
  return entrada ? t(entrada.ajuda) : undefined
}

/**
 * Os gatilhos em dois grupos.
 *
 * "O que ele fez no Telegram" e "o que aconteceu com ele no CRM" são perguntas
 * diferentes, e quem monta o fluxo pensa numa de cada vez. A lista corrida
 * misturava as duas e obrigava a ler dez itens para achar um.
 */
const GRUPOS_DE_GATILHO = [
  { titulo: 'Telegram', chaves: GATILHOS_DE_MENSAGEM },
  { titulo: 'Eventos do contato', chaves: GATILHOS_DE_EVENTO },
] as const

const STATUS_ROTULO = { rascunho: 'rascunho', ativo: 'no ar', parado: 'parado' } as const

/**
 * Automações.
 *
 * A lista é do workspace inteiro, e não de uma conta: quem abre a tela quer
 * saber "o que está no ar?", não "o que está no ar nesta conta". A conta
 * aparece em cada linha e volta a ser a primeira escolha na hora de criar,
 * porque é ela que decide o que a automação pode fazer.
 */
function AutomacoesPage() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { isAdmin } = useWorkspaceRole()
  const navigate = useNavigate()

  const automacoes = useQuery(trpc.automacao.list.queryOptions({}))
  const contas = useQuery(trpc.channel.list.queryOptions())
  const { simples } = Route.useSearch()
  const [criandoFluxo, setCriandoFluxo] = useState(false)
  const [vendoContatos, setVendoContatos] = useState<{ id: string; nome: string } | null>(null)

  const ativas = (contas.data ?? []).filter((conta) => conta.isEnabled)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [['automacao']] })
  }

  const definirStatus = useMutation({
    ...trpc.automacao.definirStatus.mutationOptions(),
    onSuccess: invalidate,
  })
  const remover = useMutation({
    ...trpc.automacao.remove.mutationOptions(),
    onSuccess: invalidate,
  })

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        <div className="flex items-center gap-3">
          <h1 className="min-w-0 font-display text-2xl">{t('Automações')}</h1>
          {/* Uma porta de criação só. O modo simples é uma alternância dentro
              do editor, não um segundo jeito de começar: dois pontos de partida
              obrigariam a escolher o editor antes de saber o que se vai montar. */}
          {isAdmin && ativas.length > 0 && (
            <button
              type="button"
              onClick={() => setCriandoFluxo(true)}
              className="ml-auto shrink-0 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-on-accent hover:bg-accent-ink"
            >
              {t('Nova automação')}
            </button>
          )}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {t(
            'Respostas que saem sozinhas. Assim que alguém da equipe responde, as automações daquela conversa param — o robô não atropela atendimento em andamento.',
          )}
        </p>

        {!contas.isPending && ativas.length === 0 && (
          <p className="mt-6 rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-xs text-ink-3">
            {t('Conecte uma conta do Telegram para criar automações.')}
          </p>
        )}

        {!automacoes.isPending && (automacoes.data ?? []).length === 0 && ativas.length > 0 && (
          <p className="mt-6 rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-xs text-ink-3">
            {t('Nenhuma automação ainda. Crie a primeira em “Nova automação”.')}
          </p>
        )}

        <ul className="mt-6 flex flex-col gap-2">
          {(automacoes.data ?? []).map((linha) => (
            <li
              key={linha.id}
              className="flex items-start gap-3 rounded-lg border border-line bg-surface px-3 py-2.5"
            >
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="text-sm font-semibold">{linha.name}</span>
                  <span
                    className="rounded-full px-1.5 py-px text-[10px] font-semibold"
                    style={{
                      background:
                        linha.status === 'ativo' ? 'var(--accent-soft)' : 'var(--surface-2)',
                      color: linha.status === 'ativo' ? 'var(--accent-ink)' : 'var(--ink-3)',
                    }}
                  >
                    {t(STATUS_ROTULO[linha.status])}
                  </span>
                  <span className="rounded-full bg-surface-2 px-1.5 py-px text-[10px] font-semibold text-ink-3">
                    {linha.canalNome}
                  </span>
                </span>
                {/* Execuções e contatos são números diferentes e os dois
                    importam: cem execuções de dez pessoas é um fluxo que
                    repete; de cem pessoas, é um fluxo que alcança. */}
                {linha.execucoes > 0 ? (
                  <button
                    type="button"
                    onClick={() => setVendoContatos({ id: linha.id, nome: linha.name })}
                    className="mt-0.5 block text-2xs text-ink-3 underline decoration-dotted underline-offset-2 hover:text-ink-2"
                  >
                    {t('{contatos} contato(s) · {passagens} passagem(ns)', {
                      contatos: linha.contatos,
                      passagens: linha.execucoes,
                    })}
                  </button>
                ) : (
                  <span className="mt-0.5 block text-2xs text-ink-3">{t('nunca rodou')}</span>
                )}
              </span>

              {isAdmin && (
                <span className="flex flex-none gap-1.5">
                  {/* Parar em vermelho desde o repouso, contra a regra geral
                      das listas: aqui ele faz par com um "Publicar" verde, e
                      dois botões de mesma cor ao lado um do outro não diriam
                      qual é qual. A cor é o que distingue o par. */}
                  <BotaoAcao
                    variante={linha.status === 'ativo' ? 'parada' : 'positiva'}
                    onClick={() =>
                      definirStatus.mutate({
                        id: linha.id,
                        status: linha.status === 'ativo' ? 'parado' : 'ativo',
                      })
                    }
                  >
                    {linha.status === 'ativo' ? t('Parar') : t('Publicar')}
                  </BotaoAcao>
                  <BotaoAcao
                    onClick={() => navigate({ to: '/automacoes/$id', params: { id: linha.id } })}
                  >
                    {t('Editar')}
                  </BotaoAcao>
                  <BotaoAcao variante="destrutiva" onClick={() => remover.mutate({ id: linha.id })}>
                    {t('Remover')}
                  </BotaoAcao>
                </span>
              )}
            </li>
          ))}
        </ul>

        {definirStatus.error && (
          <p role="alert" className="mt-3 text-xs text-danger">
            {t(definirStatus.error.message)}
          </p>
        )}

        {ativas[0] && <Expediente inboxId={ativas[0].inboxId} />}

        {vendoContatos && (
          <ContatosDaAutomacao
            automationId={vendoContatos.id}
            nome={vendoContatos.nome}
            onFechar={() => setVendoContatos(null)}
          />
        )}

        {criandoFluxo && (
          <NovoFluxo
            contas={ativas.map((conta) => ({ id: conta.id, name: conta.name, type: conta.type }))}
            onFechar={() => setCriandoFluxo(false)}
          />
        )}

        {simples && (
          <EditorBasico
            automationId={simples}
            contas={ativas.map((conta) => ({ id: conta.id, name: conta.name, type: conta.type }))}
            onFechar={() => navigate({ to: '/automacoes', search: { simples: undefined } })}
            onSalvo={invalidate}
          />
        )}
      </div>
    </div>
  )
}

interface BotaoDoFormulario {
  chave: string
  tipo: 'link' | 'resposta'
  text: string
  url: string
}

/**
 * O editor simples: um gatilho e uma mensagem.
 *
 * Grava no **mesmo** grafo que o canvas vai editar — um fluxo de dois nós. É o
 * que impede os dois editores divergirem: quem cria aqui e abre no canvas
 * depois encontra o próprio fluxo, e não uma tradução dele.
 */
function EditorBasico({
  automationId,
  contas,
  onFechar,
  onSalvo,
}: {
  automationId: string | null
  contas: { id: string; name: string; type: 'business' | 'bot' }[]
  onFechar: () => void
  onSalvo: () => void
}) {
  const trpc = useTRPC()
  const existente = useQuery({
    ...trpc.automacao.get.queryOptions({ id: automationId ?? '' }),
    enabled: Boolean(automationId),
  })

  const [channelId, setChannelId] = useState(contas[0]?.id ?? '')
  const [nome, setNome] = useState('')
  const [gatilho, setGatilho] = useState<AutomationTrigger>('first_inbound')
  const [palavras, setPalavras] = useState('')
  const [texto, setTexto] = useState('')
  const textoRef = useRef<HTMLTextAreaElement>(null)
  const [botoes, setBotoes] = useState<BotaoDoFormulario[]>([])
  const [carregou, setCarregou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  // Carrega uma vez, quando a consulta chega. Sincronizar a cada render
  // desfaria o que a pessoa está digitando.
  if (automationId && existente.data && !carregou) {
    const noGatilho = existente.data.nos.find((no) => no.kind === 'gatilho')
    const noMensagem = existente.data.nos.find((no) => no.kind === 'mensagem')
    const cfgGatilho = (noGatilho?.config ?? {}) as ConfigGatilho
    const cfgMensagem = (noMensagem?.config ?? {}) as ConfigMensagem

    setChannelId(existente.data.fluxo.channelId)
    setNome(existente.data.fluxo.name)
    setGatilho(cfgGatilho.gatilho ?? 'first_inbound')
    setPalavras((cfgGatilho.keywords ?? []).join(', '))
    setTexto(cfgMensagem.texto ?? '')
    setBotoes(
      (cfgMensagem.botoes ?? []).map((botao, indice) => ({
        chave: `existente-${indice}`,
        tipo: botao.tipo,
        text: botao.text,
        url: 'url' in botao ? botao.url : '',
      })),
    )
    setCarregou(true)
  }

  const conta = contas.find((linha) => linha.id === channelId) ?? contas[0]
  const capacidades = capacidadesDoCanal(conta?.type ?? 'business')

  const criar = useMutation(trpc.automacao.create.mutationOptions())
  const renomear = useMutation(trpc.automacao.rename.mutationOptions())
  const salvarGrafo = useMutation(trpc.automacao.salvarGrafo.mutationOptions())

  async function submit(event: FormEvent) {
    event.preventDefault()
    setErro(null)
    setSalvando(true)

    try {
      const id =
        automationId ?? (await criar.mutateAsync({ channelId, name: nome, editor: 'basico' })).id

      if (automationId) await renomear.mutateAsync({ id, name: nome })

      const idGatilho = `nod_gatilho_${id}`
      const idMensagem = `nod_mensagem_${id}`

      const configGatilho: ConfigGatilho = {
        gatilho,
        ...(gatilho === 'palavra_chave'
          ? {
              keywords: palavras
                .split(',')
                .map((palavra) => palavra.trim())
                .filter(Boolean),
              matchMode: 'contem' as const,
            }
          : {}),
      }

      const configMensagem: ConfigMensagem = {
        texto,
        botoes: botoes.map(
          (botao): BotaoDoFluxo =>
            botao.tipo === 'link'
              ? { tipo: 'link', text: botao.text, url: botao.url }
              : { tipo: 'resposta', text: botao.text },
        ),
      }

      await salvarGrafo.mutateAsync({
        id,
        nos: [
          { id: idGatilho, kind: 'gatilho', config: configGatilho, posX: 0, posY: 0 },
          { id: idMensagem, kind: 'mensagem', config: configMensagem, posX: 280, posY: 0 },
        ],
        arestas: [{ fromNodeId: idGatilho, saida: 'then', toNodeId: idMensagem }],
      })

      onSalvo()
      onFechar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Falha ao salvar')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal
      title={automationId ? t('Editar automação') : t('Nova automação')}
      description={ajudaDoGatilho(gatilho)}
      onClose={onFechar}
    >
      <form onSubmit={submit} className="flex flex-col gap-3 p-4">
        <Campo label={t('Conta')}>
          <select
            value={channelId}
            onChange={(event) => setChannelId(event.target.value)}
            disabled={Boolean(automationId)}
            className="campo"
          >
            {contas.map((linha) => (
              <option key={linha.id} value={linha.id}>
                {linha.name} · {linha.type === 'bot' ? t('bot') : t('conta business')}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-2xs text-ink-3">
            {capacidades.botoes
              ? t('Conta de bot: aceita botões e link de entrada.')
              : t(
                  'Conta business: o Telegram não permite botão nem /start em nome dela. Um menu aqui é “responda 1, 2 ou 3”.',
                )}
          </span>
        </Campo>

        <Campo label={t('Nome')}>
          <input
            value={nome}
            onChange={(event) => setNome(event.target.value)}
            required
            maxLength={60}
            placeholder={t('Boas-vindas do Comercial')}
            className="campo"
          />
        </Campo>

        <Campo label={t('Quando disparar')}>
          <select
            value={gatilho}
            onChange={(event) => setGatilho(event.target.value as AutomationTrigger)}
            className="campo"
          >
            {/* Só os gatilhos que esta conta aceita: business não tem /start, e
                oferecê-lo seria prometer o que não acontece. */}
            {GRUPOS_DE_GATILHO.map((grupo) => {
              const disponiveis = grupo.chaves.filter((chave) =>
                capacidades.gatilhos.includes(chave),
              )
              if (disponiveis.length === 0) return null
              return (
                <optgroup key={grupo.titulo} label={t(grupo.titulo)}>
                  {disponiveis.map((chave) => (
                    <option key={chave} value={chave}>
                      {tituloDoGatilho(chave)}
                    </option>
                  ))}
                </optgroup>
              )
            })}
          </select>
        </Campo>

        {gatilho === 'palavra_chave' && (
          <Campo label={t('Palavras (separadas por vírgula)')}>
            <input
              value={palavras}
              onChange={(event) => setPalavras(event.target.value)}
              required
              placeholder={t('preço, valor, quanto custa')}
              className="campo"
            />
          </Campo>
        )}

        <Campo label={t('Mensagem')}>
          <textarea
            value={texto}
            onChange={(event) => setTexto(event.target.value)}
            required
            rows={3}
            maxLength={4000}
            placeholder={t('Olá {primeiro_nome}! Já já alguém te responde.')}
            className="campo resize-y"
            ref={textoRef}
          />
          {/*
            O mesmo "Inserir:" do canvas, e não uma frase dizendo quais chaves
            existem.

            A frase que estava aqui prometia `{{first_name}}` e `{{username}}`,
            que a substituição não reconhece mais — quem seguisse a instrução
            mandava a chave crua para o celular do cliente. Uma lista escrita à
            mão envelhece calada; os botões vêm da mesma fonte que o servidor
            usa para substituir, então não há como divergirem.
          */}
          <InserirVariavel campo={textoRef} valor={texto} onInserir={setTexto} />
        </Campo>

        {/* O campo de botão só existe onde o Telegram aceita teclado. */}
        {capacidades.botoes && (
          <Campo label={t('Botões (opcional)')}>
            <span className="flex flex-col gap-2">
              {botoes.map((botao, indice) => (
                <span key={botao.chave} className="flex flex-wrap items-center gap-1.5">
                  <select
                    value={botao.tipo}
                    onChange={(event) =>
                      setBotoes(
                        botoes.map((linha, i) =>
                          i === indice
                            ? { ...linha, tipo: event.target.value as 'link' | 'resposta' }
                            : linha,
                        ),
                      )
                    }
                    className="campo w-28"
                  >
                    <option value="resposta">{t('Resposta')}</option>
                    <option value="link">{t('Link')}</option>
                  </select>
                  <input
                    value={botao.text}
                    onChange={(event) =>
                      setBotoes(
                        botoes.map((linha, i) =>
                          i === indice ? { ...linha, text: event.target.value } : linha,
                        ),
                      )
                    }
                    required
                    maxLength={40}
                    placeholder={t('Quero saber mais')}
                    className="campo w-40"
                  />
                  {botao.tipo === 'link' && (
                    <input
                      value={botao.url}
                      onChange={(event) =>
                        setBotoes(
                          botoes.map((linha, i) =>
                            i === indice ? { ...linha, url: event.target.value } : linha,
                          ),
                        )
                      }
                      required
                      type="url"
                      placeholder="https://"
                      className="campo w-52"
                    />
                  )}
                  <BotaoAcao
                    variante="destrutiva"
                    onClick={() => setBotoes(botoes.filter((_, i) => i !== indice))}
                  >
                    {t('Remover')}
                  </BotaoAcao>
                </span>
              ))}

              {botoes.length < AUTOMACAO_MAX_BOTOES && (
                <BotaoAcao
                  className="self-start"
                  onClick={() =>
                    setBotoes([
                      ...botoes,
                      { chave: crypto.randomUUID(), tipo: 'resposta', text: '', url: '' },
                    ])
                  }
                >
                  {t('Adicionar botão')}
                </BotaoAcao>
              )}
            </span>
            <span className="mt-1 block text-2xs text-ink-3">
              {t(
                'No modo simples o clique entra na conversa como mensagem do cliente. Para cada botão levar a um caminho diferente é preciso o construtor de fluxo.',
              )}
            </span>
          </Campo>
        )}

        {erro && (
          <p role="alert" className="text-xs text-danger">
            {erro}
          </p>
        )}

        <button
          type="submit"
          disabled={salvando}
          className="self-start rounded-md bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
        >
          {salvando ? t('Salvando…') : automationId ? t('Salvar') : t('Criar automação')}
        </button>

        <p className="text-2xs leading-relaxed text-ink-3">
          {t('A automação nasce em rascunho — publique na lista quando estiver pronta.')}
        </p>
      </form>

      <style>{`
        .campo {
          width: 100%;
          border-radius: 6px;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          padding: 7px 10px;
          font-size: 13px;
          color: var(--ink);
        }
      `}</style>
    </Modal>
  )
}

/** Horário de atendimento — é ele que define o “fora do expediente”. */
function Expediente({ inboxId }: { inboxId: string }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { isAdmin } = useWorkspaceRole()
  const atual = useQuery(trpc.automacao.expediente.queryOptions({ inboxId }))

  const [rascunho, setRascunho] = useState<{
    ativo: boolean
    faixas: { ativo: boolean; inicio: string; fim: string }[]
  } | null>(null)

  const salvar = useMutation({
    ...trpc.automacao.salvarExpediente.mutationOptions(),
    onSuccess: () => {
      setRascunho(null)
      queryClient.invalidateQueries({ queryKey: [['automacao']] })
    },
  })

  const dados = rascunho ?? atual.data
  if (!dados) return null

  function mexer(indice: number, campo: 'ativo' | 'inicio' | 'fim', valor: boolean | string) {
    const faixas = dados!.faixas.map((faixa, i) =>
      i === indice ? { ...faixa, [campo]: valor } : faixa,
    )
    setRascunho({ ativo: dados!.ativo, faixas })
  }

  return (
    // Recolhido por padrão: o expediente se configura uma vez e fica anos sem
    // ser tocado, enquanto a lista de automações muda toda semana. Aberto, ele
    // empurrava para baixo justamente o que se vem ver aqui.
    <details className="mt-8 rounded-lg border border-line bg-surface [&[open]_summary]:border-b">
      <summary className="flex cursor-pointer items-center gap-3 p-4">
        <h2 className="min-w-0 font-display text-base">{t('Expediente')}</h2>
        <span className="ml-auto flex flex-none items-center gap-2 text-2xs text-ink-3">
          {dados.ativo ? t('ligado') : t('desligado')}
        </span>
      </summary>

      <div className="p-4">
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={dados.ativo}
            disabled={!isAdmin}
            onChange={(event) => setRascunho({ ...dados, ativo: event.target.checked })}
          />
          {t('Ligado')}
        </label>
        <p className="mt-1 text-2xs leading-relaxed text-ink-3">
          {t(
            'Horário de Brasília, da equipe deste inbox. Um turno que termina antes de começar atravessa a meia-noite — 18:00 às 02:00 é o plantão da madrugada.',
          )}
        </p>

        <ul className="mt-3 flex flex-col gap-1.5">
          {dados.faixas.map((faixa, indice) => (
            <li key={DIAS[indice]} className="flex items-center gap-2">
              <label className="flex w-28 flex-none items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={faixa.ativo}
                  disabled={!isAdmin}
                  onChange={(event) => mexer(indice, 'ativo', event.target.checked)}
                />
                {t(DIAS[indice] ?? '')}
              </label>
              <input
                type="time"
                value={faixa.inicio}
                disabled={!isAdmin || !faixa.ativo}
                onChange={(event) => mexer(indice, 'inicio', event.target.value)}
                className="rounded-md border border-line-strong bg-surface px-2 py-1 text-xs disabled:opacity-40"
              />
              <span className="text-2xs text-ink-3">{t('às')}</span>
              <input
                type="time"
                value={faixa.fim}
                disabled={!isAdmin || !faixa.ativo}
                onChange={(event) => mexer(indice, 'fim', event.target.value)}
                className="rounded-md border border-line-strong bg-surface px-2 py-1 text-xs disabled:opacity-40"
              />
            </li>
          ))}
        </ul>

        {salvar.error && (
          <p role="alert" className="mt-2 text-xs text-danger">
            {t(salvar.error.message)}
          </p>
        )}

        {rascunho && isAdmin && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={salvar.isPending}
              onClick={() => salvar.mutate({ inboxId, ...rascunho })}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-on-accent disabled:opacity-50"
            >
              {salvar.isPending ? t('Salvando…') : t('Salvar expediente')}
            </button>
            <BotaoAcao onClick={() => setRascunho(null)} className="px-3 py-1.5 text-xs">
              {t('Descartar')}
            </BotaoAcao>
          </div>
        )}
      </div>
    </details>
  )
}

/**
 * Cria o fluxo e abre o canvas já nele.
 *
 * Só nome e conta: o resto se monta arrastando. Pedir gatilho aqui seria
 * perguntar duas vezes a mesma coisa, porque o primeiro bloco do canvas é
 * exatamente isso.
 */
function NovoFluxo({
  contas,
  onFechar,
}: {
  contas: { id: string; name: string; type: 'business' | 'bot' }[]
  onFechar: () => void
}) {
  const trpc = useTRPC()
  const navigate = useNavigate()
  const [nome, setNome] = useState('')
  const [channelId, setChannelId] = useState(contas[0]?.id ?? '')

  const criar = useMutation({
    ...trpc.automacao.create.mutationOptions(),
    onSuccess: (criado) => navigate({ to: '/automacoes/$id', params: { id: criado.id } }),
  })

  return (
    <Modal
      title={t('Novo fluxo')}
      description={t('A conta decide o que os blocos podem fazer — ela não muda depois.')}
      onClose={onFechar}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          criar.mutate({ channelId, name: nome, editor: 'fluxo' })
        }}
        className="flex flex-col gap-3 p-4"
      >
        <Campo label={t('Nome')}>
          <input
            value={nome}
            onChange={(event) => setNome(event.target.value)}
            required
            maxLength={60}
            placeholder={t('Triagem do Comercial')}
            className="campo"
          />
        </Campo>

        <Campo label={t('Conta')}>
          <select
            value={channelId}
            onChange={(event) => setChannelId(event.target.value)}
            className="campo"
          >
            {contas.map((conta) => (
              <option key={conta.id} value={conta.id}>
                {conta.name} · {conta.type === 'bot' ? t('bot') : t('conta business')}
              </option>
            ))}
          </select>
        </Campo>

        {criar.error && (
          <p role="alert" className="text-xs text-danger">
            {t(criar.error.message)}
          </p>
        )}

        <button
          type="submit"
          disabled={criar.isPending}
          className="self-start rounded-md bg-accent px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50"
        >
          {criar.isPending ? t('Criando…') : t('Criar e abrir o construtor')}
        </button>
      </form>

      <style>{`
        .campo {
          width: 100%;
          border-radius: 6px;
          border: 1px solid var(--line-strong);
          background: var(--surface);
          padding: 7px 10px;
          font-size: 13px;
          color: var(--ink);
        }
      `}</style>
    </Modal>
  )
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-2xs font-bold uppercase tracking-wider text-ink-3">
        {label}
      </span>
      {children}
    </label>
  )
}
